// v2 parrot — a true macaw: a plump chested body, a big head with the bare white
// face patch and a three-part hooked beak (upper hook, curled tip, lower
// mandible), two layered folded wing panels per side in contrast colours, a long
// stepped three-feather tail and little gripping feet. Four deterministic
// plumages: scarlet, blue-and-gold, military green and a sun conure orange.
// Ported 1:1 from age-of-mcp's v2/parrot.ts, using the library's shared `safari`
// (eyes) helper.
import * as THREE from "three";
import { defineModel, safari, type Part } from "../../lib";

const PLUMES = [
  { body: "#d8362e", chest: "#e04a38", wingIn: "#e8b62a", wingOut: "#2f5fc0",
    tail: "#c8302e" },
  { body: "#2e6ad0", chest: "#eab832", wingIn: "#2e6ad0", wingOut: "#1e4ea8",
    tail: "#2e6ad0" },
  { body: "#3d9442", chest: "#52a44e", wingIn: "#2e7d36", wingOut: "#2a55b0",
    tail: "#b03a2e" },
  { body: "#ef8c26", chest: "#f3b32b", wingIn: "#ef8c26", wingOut: "#3aa04a",
    tail: "#d2691e" },
];

function parrotParts(v: number): Part[] {
  const c = PLUMES[v % PLUMES.length]!;
  const parts: Part[] = [
    // plump body with a brighter chest
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.body,
      position: [0.002, 0.034, 0], scale: [1.15, 1.25, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.018, 0), color: c.chest,
      position: [0.018, 0.028, 0], scale: [1.0, 1.0, 0.9] },
    // big head
    { geometry: new THREE.IcosahedronGeometry(0.0165, 0), color: c.body,
      position: [0.035, 0.061, 0] },
    // the three-part hooked beak: upper hook, curled tip, lower mandible
    { geometry: new THREE.ConeGeometry(0.0085, 0.024, 4), color: "#3a3a40",
      position: [0.0545, 0.057, 0], rotation: [0, 0, -1.95] },
    { geometry: new THREE.ConeGeometry(0.0042, 0.011, 4), color: "#3a3a40",
      position: [0.06, 0.0475, 0], rotation: [0, 0, -2.7] },
    { geometry: new THREE.ConeGeometry(0.005, 0.009, 4), color: "#4a4a50",
      position: [0.0505, 0.0505, 0], rotation: [0, 0, -0.9] },
    // long stepped tail: a centre feather flanked by two splayed shorter ones
    { geometry: new THREE.ConeGeometry(0.0085, 0.06, 4), color: c.tail,
      position: [-0.055, 0.035, 0], rotation: [0, 0, Math.PI / 2],
      scale: [1, 1, 0.45] },
    { geometry: new THREE.ConeGeometry(0.007, 0.045, 4), color: c.wingOut,
      position: [-0.048, 0.033, 0.007], rotation: [0, 0.18, Math.PI / 2],
      scale: [1, 1, 0.45] },
    { geometry: new THREE.ConeGeometry(0.007, 0.045, 4), color: c.wingOut,
      position: [-0.048, 0.033, -0.007], rotation: [0, -0.18, Math.PI / 2],
      scale: [1, 1, 0.45] },
  ];
  // per side: bare white face patch + two layered folded wing panels
  for (const s of [1, -1] as const) {
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.0085, 0), color: "#f0ece2",
        position: [0.0425, 0.06, s * 0.0095], scale: [0.85, 0.95, 0.45] },
      { geometry: new THREE.BoxGeometry(0.036, 0.005, 0.015), color: c.wingIn,
        position: [-0.002, 0.044, s * 0.0215],
        rotation: [-s * 0.12, s * 0.3, 0.1] },
      { geometry: new THREE.BoxGeometry(0.032, 0.004, 0.011), color: c.wingOut,
        position: [-0.02, 0.047, s * 0.0265],
        rotation: [-s * 0.1, s * 0.5, 0.16] },
      // gripping foot
      { geometry: new THREE.IcosahedronGeometry(0.0045, 0), color: "#5a5a60",
        position: [0.006, 0.004, s * 0.008] },
    );
  }
  parts.push(...safari.eyes(0.044, 0.0635, 0.0105, 0.0028, "#16130e"));
  return parts;
}

export const parrotV2 = defineModel({
  id: "parrot-v2",
  batchId: "fable",
  name: "Parrot (v2)",
  tags: ["creature","parrot","low-poly","age-of-mcp","fable"],
  summary: "Low-poly parrot.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.07,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => parrotParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => parrotParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => parrotParts(2) },
    { id: "variant-3", label: "Variant 4", parts: () => parrotParts(3) },
  ],
  animations: [],
});
